/**
 * OrderContactsField — the compact "Confirmation contacts" row in Order Info.
 *
 * The sales site's own recipients ("(sales site)", fixed) on the first line,
 * then ONE line per buyer picked for this order: name, address, and the
 * sent status of each email next to it ("Confirmation sent 10/6 3:00 PM by
 * portal", "not sent yet: order not submitted to Sage", "portal relay
 * pending") with the evidence on hover — so the desk can answer "did Kevin
 * get it?" without opening the Buyer notifications panel (portal check-in
 * 2026-10-06, kanban 6095f425). The Edit button opens
 * {@link OrderContactsModal}, where recipients are added, removed and told
 * which email(s) they get.
 *
 * The status comes from GET /portal/orders/{id}/notifications (the same
 * payload the panel reads); a send or resend in the panel announces itself
 * through orderNotificationsRefresh so this row reloads too.
 */
import React, { useEffect, useState } from 'react';
import { orderNotificationService } from '../../services/orderNotifications';
import type {
  ContactDelivery,
  NotificationContact,
  SalesSiteRecipient,
} from '../../types/orderNotifications';
import OrderContactsModal from './OrderContactsModal';
import styles from './OrderNotifications.module.css';
import { kindSuffix, normalizeContacts, summarizeFixed } from './orderContactsUtils';
import { deliveryFor, deliveryLine, deliveryTitle, deliveryTone, inlineRows } from './contactDeliveryUtils';
import { onOrderNotificationsChanged } from './orderNotificationsRefresh';

export interface OrderContactsFieldProps {
  orderId: string;
  customerNo: string | null;
  /** The order's Sage ship-to code; new contacts are remembered for it. */
  shipToCode?: string | null;
  contacts: NotificationContact[];
  /** False for read-only viewers and cancelled orders. */
  canEdit: boolean;
  onSaved: (contacts: NotificationContact[]) => void;
  /** Test/DI seam — defaults to the real service. */
  service?: Pick<
    typeof orderNotificationService,
    'listCustomerContacts' | 'createCustomerContact' | 'setOrderContacts' | 'getOrderNotifications'
  >;
}

const TONE_CLASS: Record<ReturnType<typeof deliveryTone>, string> = {
  sent: styles.deliverySent,
  failed: styles.deliveryFailed,
  pending: styles.deliveryPending,
  muted: styles.deliveryMuted,
};

const TONE_GLYPH: Record<ReturnType<typeof deliveryTone>, string> = {
  sent: '✓',
  failed: '✕',
  pending: '…',
  muted: '–',
};

export const OrderContactsField: React.FC<OrderContactsFieldProps> = ({
  orderId,
  customerNo,
  shipToCode,
  contacts: rawContacts,
  canEdit,
  onSaved,
  service = orderNotificationService,
}) => {
  const [open, setOpen] = useState(false);
  // Older snapshots lack the per-kind flags (= both); normalise once here.
  const contacts = normalizeContacts(rawContacts);
  // Bumped on every open so the dialog remounts with a fresh working copy.
  const [openNonce, setOpenNonce] = useState(0);
  const [fixed, setFixed] = useState<SalesSiteRecipient[]>([]);
  const [delivery, setDelivery] = useState<ContactDelivery[]>([]);

  // The row shows the sales site's fixed recipients, so it fetches the
  // directory (one cheap call) on mount, when the customer changes, and after
  // each save (the dialog may have created contacts). setState only happens in
  // the promise callback, never synchronously in the effect body.
  const [reloadNonce, setReloadNonce] = useState(0);
  useEffect(() => {
    if (!customerNo) return;
    let cancelled = false;
    service
      .listCustomerContacts(customerNo, orderId)
      .then((resp) => {
        if (!cancelled) setFixed(resp.sales_site_recipients ?? []);
      })
      .catch(() => {
        if (!cancelled) setFixed([]);
      });
    return () => {
      cancelled = true;
    };
  }, [customerNo, orderId, service, reloadNonce]);

  // Per-contact sent status: loaded when the picked list changes, on each
  // save, and whenever the panel reports a send for this order. A failed
  // load leaves the names without a status rather than hiding the row.
  const contactsKey = contacts.map((c) => c.email).join(',');
  const [deliveryNonce, setDeliveryNonce] = useState(0);
  useEffect(() => {
    return onOrderNotificationsChanged((changedId) => {
      if (changedId === orderId) setDeliveryNonce((n) => n + 1);
    });
  }, [orderId]);
  useEffect(() => {
    if (!contactsKey) return;
    let cancelled = false;
    service
      .getOrderNotifications(orderId)
      .then((resp) => {
        if (!cancelled) setDelivery(resp.contact_delivery ?? []);
      })
      .catch(() => {
        if (!cancelled) setDelivery([]);
      });
    return () => {
      cancelled = true;
    };
  }, [orderId, service, contactsKey, reloadNonce, deliveryNonce]);

  // No customer -> nothing fixed to show (derived, so no state write in the effect).
  const fixedSummary = summarizeFixed(customerNo ? fixed : []);

  return (
    <div className={styles.field} data-testid="order-contacts-field">
      <div className={styles.compactRow}>
        <span className={styles.contactList} data-testid="order-contacts-summary">
          {fixedSummary && (
            <span
              className={styles.compactSummaryMuted}
              title={fixed.map((f) => `${f.email} (sales site — ${f.kind})`).join('\n') || undefined}
              data-testid="order-contacts-fixed-summary"
            >
              {fixedSummary}
            </span>
          )}
          {contacts.length === 0 ? (
            <span className={styles.compactSummaryMuted}>No buyer picked</span>
          ) : (
            contacts.map((c) => {
              const all = deliveryFor(delivery, c.email);
              const inline = inlineRows(all);
              // Never leave a buyer without a status: if nothing is worth
              // printing (e.g. invoice-only on a draft), show what is left.
              const rows = inline.length > 0 ? inline : all.filter((r) => r.state !== 'opted_out');
              const suffix = kindSuffix(c).replace(/[()]/g, '').trim();
              return (
                <span key={c.email} className={styles.contactLine} data-testid="order-contact-line" data-email={c.email}>
                  <span className={styles.contactName}>{c.name || c.email}</span>
                  {c.name && <span className={styles.contactEmail}>{c.email}</span>}
                  {suffix && (
                    <span className={styles.compactSummaryMuted} data-testid="order-contact-kinds">
                      ({suffix})
                    </span>
                  )}
                  {rows.map((d) => {
                    const tone = deliveryTone(d.state);
                    return (
                      <span
                        key={d.kind}
                        className={`${styles.delivery} ${TONE_CLASS[tone]}`}
                        title={deliveryTitle(d) || undefined}
                        data-testid="order-contact-delivery"
                        data-kind={d.kind}
                        data-state={d.state}
                        data-sender={d.sender ?? undefined}
                      >
                        <span className={styles.deliveryGlyph} aria-hidden="true">{TONE_GLYPH[tone]}</span>
                        {deliveryLine(d)}
                      </span>
                    );
                  })}
                </span>
              );
            })
          )}
        </span>
        {canEdit && (
          <button
            type="button"
            className={styles.editButton}
            onClick={() => {
              setOpenNonce((n) => n + 1);
              setOpen(true);
            }}
            disabled={!customerNo}
            title={customerNo ? undefined : 'Pick a customer on the order first'}
            data-testid="order-contacts-add"
            data-action="order_contacts_edit_open"
            data-target-order-id={orderId}
            data-outcome="open"
          >
            {contacts.length === 0 ? '+ Add buyer' : 'Edit'}
          </button>
        )}
        {canEdit && !customerNo && (
          <span className={styles.hint} data-testid="order-contacts-no-customer">
            Pick a customer on the order first
          </span>
        )}
      </div>
      {canEdit && (
        <OrderContactsModal
          key={openNonce}
          isOpen={open}
          onClose={() => setOpen(false)}
          orderId={orderId}
          customerNo={customerNo}
          shipToCode={shipToCode}
          contacts={contacts}
          onSaved={(saved) => {
            onSaved(saved);
            setReloadNonce((n) => n + 1);
          }}
          service={service}
        />
      )}
    </div>
  );
};

export default OrderContactsField;
